// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xCommonLoadingOverlay } from "@keller/App/Handlers/XNG/Common";
import {
    CheckoutStep,
    CHECKOUT_STEPS,
    nextStepHandler,
    previousStepHandler,
} from "@keller/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@keller/App/Widgets/CheckoutStepper/StepperContext";
import { color, doScroll, xScreen } from "@keller/XDL/Utilities";
import React, { useContext } from "react";
import { connect, ResolveThunks, shallowEqual } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapDispatchToProps = {
    xCommonLoadingOverlay,
    updateCart: makeHandlerChainAwaitable(updateCart),
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutStepperStyles {
    container?: InjectableCss;
    prev?: ButtonPresentationProps;
    next?: ButtonPresentationProps;
}

export const styles: CheckoutStepperStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            margin-top: 48px;
            padding-top: 24px;
            border-top: 1px solid ${color("common.border")};
            ${xScreen("lte", "xs")} {
                flex-direction: column-reverse;
                justify-content: center;
                padding-left: 44px;
                padding-right: 44px;
            }
        `,
    },
    prev: {
        variant: "secondary",
        sizeVariant: "small",
        mergeCss: true,
        css: css`
            ${xScreen("lte", "xs")} {
                margin-top: 20px;
            }
        `,
    },
    next: {
        variant: "primary",
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
};

const CheckoutStepper: React.FC<Props> = props => {
    const { currentStep, steps, setCurrentStep, checkoutInfoState } = useContext(StepperContext);
    const history = useHistory();

    const { cartState, checkoutShippingPage } = useAppSelector(state => {
        const cartId = state.pages.checkoutReviewAndSubmit.cartId;
        return {
            cartState: cartId ? getCartState(state, cartId) : getCurrentCartState(state),
            checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
        };
    }, shallowEqual);

    const cartPage = useAppSelector(state => getPageLinkByPageType(state, "CartPage"), shallowEqual);
    const isLoading = cartState.isLoading;
    const cart = cartState.value;
    const showPlaceOrderButton = canPlaceOrder(cartState.value);

    const showSubmitForApprovalOrder = useAppSelector(state =>
        canSubmitForApprovalOrder(getCurrentCartState(state).value),
    );

    const goToNextStep = async (nextStep: CheckoutStep) => {
        if (currentStep === CHECKOUT_STEPS.SHIPPING) {
            props.xCommonLoadingOverlay({ key: "add", value: "UpdateCart_Shipping" });
            await props.updateCart({ fullExpand: true });
            props.xCommonLoadingOverlay({ key: "clear", value: "UpdateCart_Shipping" });
        }

        if (currentStep !== CHECKOUT_STEPS.ADDRESS) {
            setCurrentStep(nextStep.index);
        }

        doScroll(true);
    };

    const goToPrevStep = (prevStep: CheckoutStep) => {
        if (currentStep === CHECKOUT_STEPS.ADDRESS) {
            history.push(cartPage?.url as string);
        } else if (prevStep.index === CHECKOUT_STEPS.ADDRESS) {
            history.push(checkoutShippingPage?.url as string);
        } else {
            setCurrentStep(prevStep.index);
        }

        doScroll(true);
    };

    const prevStep = previousStepHandler(steps, currentStep);
    const nextStep = nextStepHandler(steps, currentStep);

    const isNextButtonDisabled = () => {
        if (currentStep < 0) {
            return true;
        }
        if (isLoading) {
            return true;
        }
        // XNG-Changes TOR-441: disable next button when paymentMethod doesn't select and currentStep = PAYMENT
        if (currentStep === CHECKOUT_STEPS.PAYMENT && !checkoutInfoState.paymentMethod) {
            return true;
        }
        return false;
    };

    if (currentStep === CHECKOUT_STEPS.REVIEW) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <Button
                {...styles.prev}
                onClick={() => goToPrevStep(prevStep)}
                disabled={isLoading}
                type={currentStep === CHECKOUT_STEPS.ADDRESS ? "submit" : "button"}
            >
                {translate("Back")}
            </Button>

            {currentStep !== CHECKOUT_STEPS.REVIEW && (
                <Button
                    {...styles.next}
                    onClick={() => goToNextStep(nextStep)}
                    disabled={isNextButtonDisabled()}
                    type={currentStep === CHECKOUT_STEPS.ADDRESS ? "submit" : "button"}
                >
                    {translate("Continue to ") + nextStep.name}
                </Button>
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showPlaceOrderButton && (
                <PlaceOrderButton styles={styles.next} />
            )}
            {currentStep === CHECKOUT_STEPS.REVIEW && cart && showSubmitForApprovalOrder && (
                <SubmitForApprovalButton extendedStyles={styles.next} />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CheckoutStepper),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Stepper Actions",
    },
};

export default widgetModule;
